import React, { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import api, { formatApiError } from "@/lib/api";
import { Link } from "react-router-dom";
import { Users, BookOpen, ChartLine, GraduationCap, ArrowRight, Stack, CheckCircle } from "@phosphor-icons/react";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/stats");
        setStats(data);
      } catch (e) {
        setError(formatApiError(e, "Could not load stats."));
      }
    })();
  }, []);

  const cards = stats
    ? [
        { label: "Total users", value: stats.total_users, icon: Users },
        { label: "Employees", value: stats.total_employees, icon: GraduationCap },
        { label: "Courses", value: stats.total_courses, icon: BookOpen },
        { label: "Published", value: stats.total_published, icon: CheckCircle },
        { label: "Enrollments", value: stats.total_enrollments, icon: Stack },
        { label: "Lessons completed", value: stats.total_completed_lessons, icon: ChartLine },
      ]
    : [];

  return (
    <AppLayout
      title="Admin Overview"
      eyebrow="Control room"
      actions={
        <Link
          to="/admin/courses"
          className="px-4 py-2 bg-[#E25822] hover:bg-[#C44E1A] text-white text-sm font-medium inline-flex items-center gap-2"
          data-testid="admin-overview-manage-courses"
        >
          Manage courses <ArrowRight size={16} weight="bold" />
        </Link>
      }
    >
      {error && <div className="text-sm text-[#993333] mb-4">{error}</div>}

      {/* Control room: dense grid of stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 -mr-px -mb-px mb-10">
        {cards.map((s) => (
          <div
            key={s.label}
            className="border border-[#EAEAE6] -mr-px -mb-px p-5 bg-white"
            data-testid={`admin-stat-${s.label.toLowerCase().replace(/\s/g, "-")}`}
          >
            <div className="flex items-start justify-between">
              <div className="eyebrow">{s.label}</div>
              <s.icon size={18} weight="duotone" color="#E25822" />
            </div>
            <div className="font-heading text-3xl font-semibold tracking-tight mt-2">{s.value}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="border border-[#EAEAE6] bg-white">
          <div className="px-5 py-4 border-b border-[#EAEAE6] flex items-center justify-between">
            <div className="font-heading font-bold tracking-tight">Top courses</div>
            <Link
              to="/admin/courses"
              className="eyebrow text-[#E25822] hover:text-[#C44E1A]"
              data-testid="admin-view-all-courses"
            >
              View all
            </Link>
          </div>
          {!stats || stats.top_courses.length === 0 ? (
            <div className="p-6 text-sm text-[#8A8A85]">No enrollments yet.</div>
          ) : (
            <div className="divide-y divide-[#EAEAE6]">
              {stats.top_courses.map((c) => (
                <div key={c.course_id} className="flex items-center justify-between px-5 py-4">
                  <div className="font-semibold">{c.title}</div>
                  <div className="font-mono text-sm text-[#5C5C58]">{c.enrollments} enrolled</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="border border-[#EAEAE6] bg-white p-6">
          <div className="eyebrow mb-3">Quick actions</div>
          <div className="space-y-3">
            <Link
              to="/admin/courses"
              className="flex items-center justify-between border border-[#EAEAE6] px-4 py-3 hover:border-[#E25822] hover:-translate-y-0.5 transition-all"
              data-testid="quick-create-course"
            >
              <span className="font-semibold">Create new course</span>
              <ArrowRight size={16} />
            </Link>
            <Link
              to="/admin/users"
              className="flex items-center justify-between border border-[#EAEAE6] px-4 py-3 hover:border-[#E25822] hover:-translate-y-0.5 transition-all"
              data-testid="quick-manage-users"
            >
              <span className="font-semibold">Manage users</span>
              <ArrowRight size={16} />
            </Link>
            <Link
              to="/catalog"
              className="flex items-center justify-between border border-[#EAEAE6] px-4 py-3 hover:border-[#E25822] hover:-translate-y-0.5 transition-all"
              data-testid="quick-view-catalog"
            >
              <span className="font-semibold">View learner catalog</span>
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
